<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const variantExamples = [
  {
    produk: 'Kopi Susu',
    varian: ['Regular', 'Large'],
    harga: ['Rp 20.000', 'Rp 25.000'],
    sku: ['KS-REG', 'KS-LRG'],
  },
  {
    produk: 'Nasi Goreng',
    varian: ['Biasa', 'Spesial', 'Jumbo'],
    harga: ['Rp 15.000', 'Rp 20.000', 'Rp 30.000'],
    sku: ['NG-BSA', 'NG-SPL', 'NG-JMB'],
  },
  {
    produk: 'Croissant',
    varian: ['Original'],
    harga: ['Rp 18.000'],
    sku: ['CRS-ORI'],
  },
]

const fieldsProduk = [
  {
    field: 'Nama Produk',
    wajib: true,
    penjelasan: 'Nama produk yang ditampilkan di kasir dan struk. Usahakan nama yang singkat dan familiar dengan pelanggan.',
    contoh: '"Kopi Susu", "Teh Tarik", "Nasi Goreng Spesial"',
  },
  {
    field: 'Kategori',
    wajib: false,
    penjelasan: 'Pengelompokan produk. Berguna agar kasir cepat mencari produk saat order banyak.',
    contoh: '"Minuman Kopi", "Makanan Ringan", "Minuman Non-Kopi"',
  },
  {
    field: 'Deskripsi',
    wajib: false,
    penjelasan: 'Keterangan tambahan produk. Muncul di beberapa laporan. Tidak wajib diisi.',
    contoh: '"Kopi susu dengan espresso double shot, susu fresh, dan gula aren"',
  },
  {
    field: 'Urutan Tampil',
    wajib: false,
    penjelasan: 'Angka urutan produk di dalam kategorinya. Semakin kecil angkanya, semakin atas posisi produk. Berguna untuk menaruh produk best-seller di posisi teratas.',
    contoh: '1 (paling atas), 2, 3, dst.',
  },
  {
    field: 'Foto Produk',
    wajib: false,
    penjelasan: 'Gambar produk. Muncul di tampilan kasir POS agar kasir mudah mengenali produk.',
    contoh: 'File JPG atau PNG, ukuran maks. 2MB',
  },
  {
    field: 'Status Aktif',
    wajib: false,
    penjelasan: 'Produk aktif = muncul di kasir POS. Produk non-aktif = tersembunyi dari kasir (tidak bisa dijual), tapi data tetap tersimpan.',
    contoh: 'Aktif (default). Non-aktifkan produk musiman yang sedang tidak dijual.',
  },
]

const fieldsVarian = [
  {
    field: 'Nama Varian',
    wajib: true,
    penjelasan: 'Nama ukuran/jenis varian. Muncul di kasir dan struk di bawah nama produk.',
    contoh: '"Regular", "Large", "Spesial", "Tanpa Es"',
  },
  {
    field: 'SKU',
    wajib: false,
    penjelasan: 'Kode unik untuk identifikasi produk. Berguna jika Anda punya banyak produk atau ingin integrasi dengan sistem lain. Boleh dikosongkan.',
    contoh: '"KS-REG-001", "KS-LRG-001"',
  },
  {
    field: 'Harga',
    wajib: true,
    penjelasan: 'Harga jual per satuan varian ini kepada pelanggan (belum termasuk pajak). Ini adalah harga yang tercetak di struk.',
    contoh: 'Rp 20.000 untuk Regular, Rp 25.000 untuk Large',
  },
  {
    field: 'Status Aktif',
    wajib: false,
    penjelasan: 'Varian aktif = bisa dipilih di kasir. Non-aktifkan varian yang sedang tidak tersedia (bukan dihapus).',
    contoh: 'Aktif = tersedia. Non-aktif = tidak muncul di kasir.',
  },
]

const faq = [
  {
    q: 'Apa bedanya "produk" dan "varian"?',
    a: 'Produk adalah nama barang yang Anda jual, misalnya "Kopi Susu". Varian adalah pilihan ukuran atau jenis dari produk tersebut, misalnya "Regular" (Rp 20.000) dan "Large" (Rp 25.000). Setiap varian punya harga sendiri. Jika produk Anda hanya punya satu harga (tidak ada pilihan ukuran), cukup buat 1 varian saja, misalnya varian bernama "Regular" atau nama produknya langsung.',
  },
  {
    q: 'Produk saya sudah pernah dijual, apakah bisa saya hapus?',
    a: 'Jika produk sudah pernah masuk dalam transaksi, sistem akan menonaktifkan produk tersebut (bukan menghapus permanen). Ini untuk menjaga riwayat penjualan tetap akurat. Jadi struk lama yang menyebut produk tersebut tidak akan rusak. Solusi jika produk sudah tidak dijual lagi: cukup set status ke "Non-aktif" agar tidak muncul di kasir.',
  },
  {
    q: 'Bagaimana cara mengatur produk agar muncul paling atas di kasir?',
    a: 'Isi field "Urutan Tampil" dengan angka kecil. Contoh: produk best-seller isi dengan angka 1, produk biasa isi 10, produk jarang dipesan isi 99. Produk dengan angka urutan terkecil akan muncul paling atas dalam kategorinya di tampilan kasir.',
  },
  {
    q: 'Bisakah satu produk punya foto berbeda per varian?',
    a: 'Tidak. Foto produk berlaku untuk semua varian dalam produk yang sama. Satu produk punya satu foto. Jika Anda ingin foto berbeda, buat produk terpisah.',
  },
  {
    q: 'Saya ingin tambah varian baru ke produk yang sudah ada, bagaimana caranya?',
    a: 'Buka halaman Edit Produk, lalu pindah ke tab "Varian & Recipe". Di situ ada tombol "+ Tambah Varian". Varian baru langsung tersedia di kasir setelah disimpan.',
  },
  {
    q: 'Apa itu Recipe/BOM di tab Varian?',
    a: 'Recipe (atau BOM — Bill of Materials) adalah daftar bahan baku yang digunakan untuk membuat 1 porsi varian tersebut. Contoh: varian "Regular Kopi Susu" butuh 20 gram kopi + 200 ml susu + 15 gram gula. Dengan mengisi recipe, sistem bisa otomatis mengurangi stok bahan baku setiap kali produk terjual. Ini juga digunakan untuk menghitung HPP (Harga Pokok Penjualan).',
  },
  {
    q: 'Apakah produk non-aktif masih terhitung di laporan?',
    a: 'Ya. Riwayat penjualan produk tersebut sebelum dinonaktifkan tetap ada di laporan. Hanya saja produk tidak bisa dijual lagi (tidak muncul di kasir) selama status non-aktif.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Produk &amp; Inventory</span>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Produk</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4 flex-wrap gap-3">
          <VAvatar color="info" variant="tonal" size="64" rounded="lg">
            <VIcon icon="ri-cup-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Manajemen Produk</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola katalog produk, varian harga, foto, dan recipe bahan baku
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line" class="mb-0">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda menambah, mengatur, dan mengelola semua produk yang dijual di toko Anda.
          Setelah produk dibuat, kasir bisa langsung memilih dan menjualnya lewat menu POS.
        </VAlert>
      </VCol>
    </VRow>

    <!-- ── KONSEP ──────────────────────────────────────────────────────── -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="info" />
            Apa itu Produk dan Varian?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Produk</strong> adalah semua barang atau minuman yang Anda jual kepada pelanggan.
              Contoh: "Kopi Susu", "Teh Tarik", "Nasi Goreng", "Croissant".
            </p>

            <p class="text-body-2 mb-3">
              <strong>Varian</strong> adalah pilihan ukuran, jenis, atau kemasan dari sebuah produk.
              Bayangkan seperti menu di coffee shop — satu nama minuman bisa punya ukuran berbeda
              dengan harga berbeda pula.
            </p>

            <VAlert type="success" variant="tonal" class="mb-4">
              <p class="text-body-2 mb-1">
                <strong>Contoh mudah:</strong>
              </p>
              <p class="text-body-2 mb-0">
                Produk: <strong>Kopi Susu</strong><br>
                → Varian <strong>Regular</strong> = Rp 20.000<br>
                → Varian <strong>Large</strong> = Rp 25.000<br><br>
                Saat kasir menerima order "1 Kopi Susu Large", mereka tinggal pilih produk
                "Kopi Susu" lalu pilih varian "Large". Harga langsung otomatis Rp 25.000.
              </p>
            </VAlert>

            <p class="text-body-2 mb-3">
              Jika produk Anda <strong>tidak punya pilihan ukuran</strong> (misalnya hanya ada satu harga),
              tetap buat 1 varian saja — misalnya varian bernama "Normal" atau "Porsi".
              Semua produk di PintuKasir wajib punya minimal 1 varian.
            </p>

            <VTable class="mt-2">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th>Varian</th>
                  <th>Harga</th>
                  <th>SKU (opsional)</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="item in variantExamples" :key="item.produk">
                  <tr
                    v-for="(varian, idx) in item.varian"
                    :key="`${item.produk}-${varian}`"
                  >
                    <td>
                      <strong v-if="idx === 0">{{ item.produk }}</strong>
                      <span v-else class="text-medium-emphasis text-caption">↳</span>
                    </td>
                    <td>{{ varian }}</td>
                    <td>{{ item.harga[idx] }}</td>
                    <td>
                      <VChip size="x-small" color="secondary" variant="tonal">
                        {{ item.sku[idx] }}
                      </VChip>
                    </td>
                  </tr>
                </template>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── CARA TAMBAH PRODUK BARU ────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Menambah Produk Baru
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Ikuti langkah-langkah berikut untuk menambahkan produk baru ke katalog toko Anda.
              Proses ini biasanya hanya butuh waktu 2–3 menit per produk.
            </p>

            <VTimeline side="end" density="compact" truncate-line="both">
              <VTimelineItem dot-color="primary" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-primary">Langkah 1</span>
                </template>
                <p class="text-body-2 mb-0">
                  Buka menu <strong>Katalog → Produk</strong> dari sidebar kiri.
                  Anda akan melihat daftar semua produk yang sudah ada.
                </p>
              </VTimelineItem>

              <VTimelineItem dot-color="primary" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-primary">Langkah 2</span>
                </template>
                <p class="text-body-2 mb-0">
                  Klik tombol <strong>"+ Tambah Produk"</strong> di pojok kanan atas.
                  Anda akan masuk ke halaman form produk baru.
                </p>
              </VTimelineItem>

              <VTimelineItem dot-color="info" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-info">Langkah 3</span>
                </template>
                <div>
                  <p class="text-body-2 mb-2">
                    Isi <strong>Informasi Produk</strong> (bagian kiri halaman):
                  </p>
                  <ul class="text-body-2 ps-4">
                    <li class="mb-1">
                      <strong>Nama Produk</strong> — contoh: "Kopi Susu Gula Aren"
                    </li>
                    <li class="mb-1">
                      <strong>Kategori</strong> — pilih kategori yang sesuai (opsional)
                    </li>
                    <li class="mb-1">
                      <strong>Deskripsi</strong> — keterangan singkat produk (opsional)
                    </li>
                    <li>
                      <strong>Urutan Tampil</strong> — isi angka kecil untuk produk favorit (opsional)
                    </li>
                  </ul>
                </div>
              </VTimelineItem>

              <VTimelineItem dot-color="info" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-info">Langkah 4</span>
                </template>
                <div>
                  <p class="text-body-2 mb-2">
                    Isi bagian <strong>Varian &amp; Harga</strong> (bagian bawah kiri):
                  </p>
                  <ul class="text-body-2 ps-4">
                    <li class="mb-1">
                      Sistem sudah otomatis membuat 1 varian bernama "Regular"
                    </li>
                    <li class="mb-1">
                      Isi <strong>Nama Varian</strong> (contoh: "Regular" atau "Porsi")
                    </li>
                    <li class="mb-1">
                      Isi <strong>Harga</strong> dalam Rupiah — contoh: 20000 (artinya Rp 20.000)
                    </li>
                    <li class="mb-1">
                      Isi <strong>SKU</strong> jika diperlukan, boleh dikosongkan
                    </li>
                    <li>
                      Klik <strong>"+ Tambah Varian"</strong> jika produk punya lebih dari satu harga
                    </li>
                  </ul>
                </div>
              </VTimelineItem>

              <VTimelineItem dot-color="warning" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-warning">Langkah 5</span>
                </template>
                <div>
                  <p class="text-body-2 mb-2">
                    Di bagian <strong>kanan halaman</strong>:
                  </p>
                  <ul class="text-body-2 ps-4">
                    <li class="mb-1">
                      Upload <strong>Foto Produk</strong> jika ada (opsional, maks. 2MB)
                    </li>
                    <li>
                      Pastikan toggle <strong>"Produk Aktif"</strong> dalam posisi menyala (hijau)
                    </li>
                  </ul>
                </div>
              </VTimelineItem>

              <VTimelineItem dot-color="success" size="small">
                <template #opposite>
                  <span class="text-caption font-weight-bold text-success">Langkah 6</span>
                </template>
                <p class="text-body-2 mb-0">
                  Klik tombol <strong>"Simpan Produk"</strong> di pojok kanan bawah.
                  Produk langsung tersedia di kasir POS! ✅
                </p>
              </VTimelineItem>
            </VTimeline>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── PENJELASAN FIELD PRODUK ────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-list-check-2" class="me-2" color="warning" />
            Penjelasan Detail Setiap Field
          </VCardTitle>
          <VCardText>
            <h6 class="text-h6 font-weight-bold mb-3">
              A. Informasi Produk
            </h6>
            <VTable class="mb-6">
              <thead>
                <tr>
                  <th style="min-width: 130px">
                    Field
                  </th>
                  <th style="min-width: 80px">
                    Wajib?
                  </th>
                  <th>Penjelasan</th>
                  <th style="min-width: 160px">
                    Contoh
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in fieldsProduk" :key="f.field">
                  <td>
                    <strong>{{ f.field }}</strong>
                  </td>
                  <td>
                    <VChip
                      :color="f.wajib ? 'error' : 'default'"
                      size="x-small"
                      variant="tonal"
                    >
                      {{ f.wajib ? '✅ Wajib' : '❌ Opsional' }}
                    </VChip>
                  </td>
                  <td class="text-body-2 py-2">
                    {{ f.penjelasan }}
                  </td>
                  <td class="text-body-2 py-2 text-medium-emphasis">
                    {{ f.contoh }}
                  </td>
                </tr>
              </tbody>
            </VTable>

            <h6 class="text-h6 font-weight-bold mb-3">
              B. Varian &amp; Harga
            </h6>
            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 130px">
                    Field
                  </th>
                  <th style="min-width: 80px">
                    Wajib?
                  </th>
                  <th>Penjelasan</th>
                  <th style="min-width: 160px">
                    Contoh
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in fieldsVarian" :key="f.field">
                  <td>
                    <strong>{{ f.field }}</strong>
                  </td>
                  <td>
                    <VChip
                      :color="f.wajib ? 'error' : 'default'"
                      size="x-small"
                      variant="tonal"
                    >
                      {{ f.wajib ? '✅ Wajib' : '❌ Opsional' }}
                    </VChip>
                  </td>
                  <td class="text-body-2 py-2">
                    {{ f.penjelasan }}
                  </td>
                  <td class="text-body-2 py-2 text-medium-emphasis">
                    {{ f.contoh }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── EDIT PRODUK ────────────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Cara Mengedit Produk yang Sudah Ada
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Halaman edit produk memiliki <strong>2 tab</strong> terpisah agar lebih mudah dikelola.
            </p>

            <VRow>
              <VCol cols="12" md="6">
                <VCard variant="outlined" color="info">
                  <VCardTitle class="pa-3 pb-1 text-body-1">
                    <VIcon icon="ri-information-line" size="18" class="me-1" />
                    Tab 1: Informasi Produk
                  </VCardTitle>
                  <VCardText class="text-body-2">
                    Berisi form nama, kategori, deskripsi, foto, urutan tampil, dan status aktif.
                    Perubahan di sini <strong>tidak mempengaruhi harga</strong> yang sudah ada.
                    Klik <strong>"Simpan Perubahan"</strong> setelah edit.
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="6">
                <VCard variant="outlined" color="warning">
                  <VCardTitle class="pa-3 pb-1 text-body-1">
                    <VIcon icon="ri-price-tag-3-line" size="18" class="me-1" />
                    Tab 2: Varian &amp; Recipe
                  </VCardTitle>
                  <VCardText class="text-body-2">
                    Menampilkan semua varian produk beserta harganya.
                    Anda bisa tambah varian baru atau klik <strong>"Kelola Recipe (BOM)"</strong>
                    untuk mengatur bahan baku per varian.
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <ol class="text-body-2 ps-4 mt-4">
              <li class="mb-2">
                Di halaman daftar Produk, klik ikon <strong>pensil (✏️)</strong> di baris produk yang ingin diedit
              </li>
              <li class="mb-2">
                Pilih tab yang ingin Anda ubah: <strong>Informasi Produk</strong> atau <strong>Varian &amp; Recipe</strong>
              </li>
              <li class="mb-2">
                Lakukan perubahan yang diperlukan
              </li>
              <li>
                Klik <strong>"Simpan Perubahan"</strong>
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── RECIPE / BOM ───────────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="info" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-restaurant-line" class="me-2" />
            🍳 Cara Mengisi Recipe (BOM) untuk Hitung Stok Otomatis
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Recipe</strong> (atau BOM — Bill of Materials) adalah daftar bahan baku
              yang dibutuhkan untuk membuat <strong>1 porsi/cup</strong> dari sebuah varian produk.
            </p>

            <p class="text-body-2 mb-3">
              Dengan mengisi recipe, setiap kali kasir menjual 1 produk, sistem akan
              <strong>otomatis mengurangi stok bahan baku</strong> sesuai recipe yang Anda isi.
              Tidak perlu hitung manual lagi!
            </p>

            <VAlert type="success" variant="tonal" class="mb-4">
              <p class="text-body-2 mb-1">
                <strong>Contoh recipe Kopi Susu Regular:</strong>
              </p>
              <ul class="text-body-2 mb-0 ps-4">
                <li>Kopi Arabika: 20 gram</li>
                <li>Susu Full Cream: 200 ml</li>
                <li>Gula Aren Cair: 30 ml</li>
                <li>Es Batu: 150 gram</li>
              </ul>
              <p class="text-body-2 mt-2 mb-0">
                → Setiap 1 cup Kopi Susu Regular terjual, stok ke-4 bahan di atas otomatis berkurang.
              </p>
            </VAlert>

            <p class="text-body-2 font-weight-bold mb-2">
              Cara mengisi recipe:
            </p>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Buka <strong>Edit Produk</strong> → tab <strong>Varian &amp; Recipe</strong>
              </li>
              <li class="mb-2">
                Klik tombol <strong>"Kelola Recipe (BOM)"</strong> di card varian yang diinginkan
              </li>
              <li class="mb-2">
                Klik <strong>"+ Tambah Bahan"</strong>
              </li>
              <li class="mb-2">
                Pilih <strong>nama bahan</strong> dari dropdown (bahan harus sudah diinput di menu Bahan Baku)
              </li>
              <li class="mb-2">
                Isi <strong>jumlah</strong> yang digunakan per porsi — contoh: 20 (untuk 20 gram)
              </li>
              <li class="mb-2">
                Ulangi untuk semua bahan
              </li>
              <li>
                Klik <strong>"Simpan Recipe"</strong>
              </li>
            </ol>

            <VAlert type="warning" variant="tonal" class="mt-3">
              <strong>⚠️ Perhatian:</strong> Bahan baku harus sudah ditambahkan di menu
              <strong>Inventory → Bahan Baku</strong> sebelum bisa dipilih di recipe.
              Jika bahan yang Anda cari tidak muncul di dropdown, tambahkan dulu di halaman Bahan Baku.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── FILTER & PENCARIAN ─────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-search-line" class="me-2" color="secondary" />
            Mencari dan Memfilter Produk
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Jika Anda punya banyak produk, gunakan fitur pencarian dan filter untuk menemukan
              produk dengan cepat.
            </p>
            <VRow>
              <VCol cols="12" sm="6">
                <div class="d-flex align-start gap-3 mb-4">
                  <VAvatar color="primary" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-search-line" size="20" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-1">
                      Kolom Pencarian
                    </p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Ketik sebagian nama produk. Sistem langsung menyaring secara otomatis
                      tanpa perlu tekan Enter.
                      Contoh: ketik "kopi" untuk tampilkan semua produk dengan kata "kopi".
                    </p>
                  </div>
                </div>
              </VCol>
              <VCol cols="12" sm="6">
                <div class="d-flex align-start gap-3">
                  <VAvatar color="info" variant="tonal" size="36" rounded>
                    <VIcon icon="ri-filter-line" size="20" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-1">
                      Filter Status
                    </p>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                      Pilih "Aktif" untuk hanya tampilkan produk yang sedang dijual.
                      Pilih "Non-aktif" untuk tampilkan produk yang disembunyikan dari kasir.
                      Pilih "Semua Status" untuk tampilkan semuanya.
                    </p>
                  </div>
                </div>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── PERINGATAN ─────────────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="error" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-error-warning-line" class="me-2" />
            ⚠️ Hal Penting yang Perlu Diperhatikan
          </VCardTitle>
          <VCardText>
            <VList density="compact" class="bg-transparent">
              <VListItem prepend-icon="ri-close-circle-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Produk yang pernah dijual tidak bisa dihapus permanen.</strong>
                  Sistem akan otomatis menonaktifkannya. Ini normal — bertujuan agar laporan
                  penjualan lama tetap akurat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Harga yang sudah tersimpan tidak berubah retroaktif.</strong>
                  Jika Anda update harga varian dari Rp 20.000 menjadi Rp 22.000,
                  transaksi lama tetap tercatat Rp 20.000. Harga baru hanya berlaku untuk
                  transaksi setelah perubahan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Setiap produk wajib minimal 1 varian dengan harga.</strong>
                  Produk tanpa varian tidak bisa dijual di kasir.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── TIPS ───────────────────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="success" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips &amp; Praktik Terbaik
          </VCardTitle>
          <VCardText>
            <VList density="compact" class="bg-transparent">
              <VListItem prepend-icon="ri-star-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Upload foto produk untuk mempercepat kasir.</strong>
                  Kasir yang melayani banyak pelanggan lebih cepat memilih produk jika ada foto.
                  Terutama jika produk Anda banyak dan namanya mirip-mirip.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Gunakan "Urutan Tampil" untuk menonjolkan best-seller.</strong>
                  Set produk terlaris dengan angka urutan 1–5 agar muncul paling atas di kasir.
                  Kasir tidak perlu scroll jauh untuk menemukan produk yang sering dipesan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Nonaktifkan, jangan hapus.</strong>
                  Jika ada produk yang sementara tidak dijual (misalnya menu musiman),
                  nonaktifkan saja. Aktifkan kembali saat musimnya tiba.
                  Data produk tetap tersimpan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line" class="px-0">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Isi recipe sesegera mungkin.</strong>
                  Semakin cepat recipe diisi, semakin akurat data stok bahan baku Anda.
                  Tanpa recipe, sistem tidak bisa mengurangi stok otomatis saat produk terjual.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- ── FAQ ────────────────────────────────────────────────────────── -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- ── LINK TERKAIT ───────────────────────────────────────────────── -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Kategori Produk', sub: 'Buat kategori sebelum menambah produk', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/produk-inventory/kategori' },
              { title: 'Bahan Baku', sub: 'Input bahan baku agar bisa dipakai di recipe', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Modifier Groups', sub: 'Tambahan seperti topping dan level manis', icon: 'ri-checkbox-multiple-line', color: 'warning', to: '/userguide/produk-inventory/modifier' },
              { title: 'POS Kasir', sub: 'Cara menjual produk lewat kasir', icon: 'ri-shopping-cart-line', color: 'success', to: '/userguide/operasional/pos-kasir' },
            ]"
            :key="link.title"
            cols="12"
            sm="6"
            md="3"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover class="h-100">
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
